import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { listJobSchedules, deleteSchedule } from '../services/scheduleService';
import { getJobsByScheduleId } from '../services/jobService';
import DeleteScheduleModal from '../components/DeleteScheduleModal';
import ErrorModal from '../components/ErrorModal';
import { searchFranchisees } from '../services/franchiseeService';
import { searchClients as searchSuppliers } from '../services/clientService';
import { searchHouseholds as searchAppClients } from '../services/householdService';
import { debounce } from 'lodash';
import { FaPlus, FaEdit, FaTrashAlt, FaCalendarAlt, FaClock, FaUserTie, FaUser, FaHome, FaMapMarkerAlt, FaDollarSign, FaHourglassHalf, FaTasks, FaLayerGroup, FaStickyNote, FaFileInvoiceDollar, FaFilter, FaTimes, FaArrowLeft, FaArrowRight, FaMedkit, FaSort, FaSortUp, FaSortDown, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { terminology, formatters } from '../config/terminology';
import { useAppConfig } from '../context/AppConfigContext';
import { Button } from '../components/forms';
import { isDemoMode } from '../utils/demoMode';

const PAGE_SIZE = 20;

// Helper function to format Firestore Timestamp
const formatTimestamp = (timestamp, includeTime = false) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  const date = new Date(timestamp._seconds * 1000);
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true; // Use AM/PM
  }
  return date.toLocaleDateString('en-AU', options);
};

// Re-use useSearch hook (copied from JobsListPage, consider moving to a shared hooks file later)
const useSearch = (searchFunction, options = {}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const optionsString = JSON.stringify(options);

    const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
        if (!searchFunction || term.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        setIsSearching(true);
        try {
            const data = await searchFunction({ searchString: term, ...fetchOptions });
            const results = data.franchisees || data.clients || data.households || [];
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearching(false);
        }
    }, 300), [searchFunction]);

    useEffect(() => {
        const currentOptions = JSON.parse(optionsString);
        debouncedFetch(searchTerm, currentOptions);
        return () => debouncedFetch.cancel();
    }, [searchTerm, optionsString, debouncedFetch]);

    return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

// Helper to get status object (including color) from config
const getStatusDetails = (statusCode, statusesFromConfig) => {
  const defaultStatus = { code: statusCode, display: statusCode || 'N/A', color: 'gray' };
  if (!statusCode || !statusesFromConfig || statusesFromConfig.length === 0) {
    return defaultStatus;
  }
  const statusObj = statusesFromConfig.find(s => s.code === statusCode);
  return statusObj ? { ...statusObj, color: statusObj.color || 'gray' } : defaultStatus;
};

// Helper to get quote status object (including color) from config
const getQuoteStatusDetails = (quoteStatus, statusesFromConfig) => {
  const defaultStatus = { code: quoteStatus, display: formatters.formatQuoteStatus(quoteStatus), color: 'gray' };
  if (!quoteStatus || !statusesFromConfig || statusesFromConfig.length === 0) {
    return defaultStatus;
  }
  const statusObj = statusesFromConfig.find(s => s.code === quoteStatus);
  return statusObj ? { ...statusObj, display: formatters.formatQuoteStatus(quoteStatus), color: statusObj.color || 'gray' } : defaultStatus;
};

// Helper to generate Tailwind classes for status pill
const getStatusPillClasses = (color) => {
  const colorMap = {
    yellow: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    cyan: 'bg-cyan-100 text-cyan-800 border border-cyan-300',
    blue: 'bg-blue-100 text-blue-800 border border-blue-300',
    green: 'bg-green-100 text-green-800 border border-green-300',
    orange: 'bg-orange-100 text-orange-800 border border-orange-300',
    red: 'bg-red-100 text-red-800 border border-red-300',
    gray: 'bg-gray-100 text-gray-800 border border-gray-300',
    gold: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  };
  return colorMap[color?.toLowerCase()] || colorMap.gray;
};

const JobSchedulerPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDirection, setLoadingDirection] = useState(null); // Add this state
  const [error, setError] = useState(null);
  const [noMoreResults, setNoMoreResults] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  const dropdownRef = useRef(null); // For potential future dropdowns

  // --- Delete modal states ---
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [jobsToDelete, setJobsToDelete] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [fetchingJobs, setFetchingJobs] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  // --- End Delete modal states ---

  // --- Filter State ---
  const [filterFseId, setFilterFseId] = useState(null);
  const [filterClientId, setFilterClientId] = useState(null);
  const [filterHouseholdId, setFilterHouseholdId] = useState(null);
  const [filterQuoteStatus, setFilterQuoteStatus] = useState('');
  const [fseSearchTerm, setFseSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [householdSearchTerm, setHouseholdSearchTerm] = useState('');
  // --- End Filter State ---

  // --- Sorting State ---
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc'); // Default: most recent first
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);

  const sortFieldOptions = [
      { id: 'franchiseeInfo.franchiseName', label: 'Franchisee', defaultDirection: 'asc', valueType: 'text' },
      { id: 'clientInfo.clientName', label: terminology.client.singular, defaultDirection: 'asc', valueType: 'text' },
      { id: 'householdInfo.firstName', label: terminology.household.singular, defaultDirection: 'asc', valueType: 'text' },
      { id: 'createdAt', label: 'Date Created', defaultDirection: 'asc', valueType: 'date' },
    ];
  
    const directionLabels = {
      asc: 'Ascending',
      desc: 'Descending'
    };
  
    const getDirectionDescriptor = (valueType, direction) => {
      if (valueType === 'date') {
        return direction === 'asc' ? 'Oldest → Newest' : 'Newest → Oldest';
      }
      return direction === 'asc' ? 'A → Z' : 'Z → A';
    };
  // --- End Sorting State ---

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [pageStartAfterIds, setPageStartAfterIds] = useState({ 1: null });
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageStartAfterIdsRef = useRef(pageStartAfterIds);

  useEffect(() => {
    pageStartAfterIdsRef.current = pageStartAfterIds;
  }, [pageStartAfterIds]);
  // --- End Pagination State ---

  // Updated fetchSchedules to handle pagination
  const fetchSchedules = useCallback(async (filters = {}, page = 1, isPageNavigation = false) => {
    // Don\'t clear schedules immediately on page navigation
    setLoading(true);
    setError(null);
    setNoMoreResults(false);
    
    const startAfterDocId = page === 1 ? null : pageStartAfterIdsRef.current[page];

    if (page > 1 && startAfterDocId === undefined) {
      console.error("Pagination error: Cannot determine starting point for page", page);
      setError("Pagination error. Please refresh or go back to page 1.");
      setLoading(false);
      return;
    }

    try {
      // Use passed filters or fall back to current state
      const effectiveFilters = {
        fseId: filters.fseId !== undefined ? filters.fseId : filterFseId,
        clientId: filters.clientId !== undefined ? filters.clientId : filterClientId,
        householdId: filters.householdId !== undefined ? filters.householdId : filterHouseholdId,
        quoteStatus: filters.quoteStatus !== undefined ? filters.quoteStatus : filterQuoteStatus,
      };

      const body = {
        userType: "admin",
        pageSize,
        startAfterDocId,
        fseId: effectiveFilters.fseId || null,
        clientId: effectiveFilters.clientId || null,
        householdId: effectiveFilters.householdId || null,
        quoteStatus: effectiveFilters.quoteStatus || null, // Convert empty string to null
        orderBy: sortBy,
        orderDirection: sortDirection,
      };

      // Remove null/empty properties (but keep userType)
      Object.keys(body).forEach(key => {
        if (key !== 'userType' && (body[key] === null || body[key] === '')) {
          delete body[key];
        }
      });

      console.log("Fetching schedules with body:", body); // Log the request body

      const data = await listJobSchedules(body);
      console.log("API Response:", data); // Log the API response
      const newSchedules = data.schedules || [];

      if (isPageNavigation && newSchedules.length === 0) {
        setNoMoreResults(true);
      } else {
        // Only update schedules if we have new data
        setSchedules(newSchedules);
        const nextDocId = data.nextPageDocId || null;
        setHasNextPage(!!nextDocId);

        if (page === 1) {
          setPageStartAfterIds({ 1: null, ...(nextDocId && { 2: nextDocId }) });
        } else if (nextDocId) {
          setPageStartAfterIds(prev => ({ ...prev, [page + 1]: nextDocId }));
        }

        setCurrentPage(page);
      }
    } catch (err) {
      setError(err.message || 'Failed to load job schedules.');
    } finally {
      setLoading(false);
    }
  }, [pageSize, filterFseId, filterClientId, filterHouseholdId, filterQuoteStatus, sortBy, sortDirection]);

  // Initialize filters from navigation state on mount, then fetch data
  useEffect(() => {
    if (location.state?.filters) {
      const { filters } = location.state;
      
      // Set all filter states
      setFilterFseId(filters.filterFseId || null);
      setFilterClientId(filters.filterClientId || null);
      setFilterHouseholdId(filters.filterHouseholdId || null);
      setFilterQuoteStatus(filters.filterQuoteStatus || '');
      setFseSearchTerm(filters.fseSearchTerm || '');
      setClientSearchTerm(filters.clientSearchTerm || '');
      setHouseholdSearchTerm(filters.householdSearchTerm || '');

      // Restore sorting state
      if (filters.sortBy) setSortBy(filters.sortBy);
      if (filters.sortDirection) setSortDirection(filters.sortDirection);

      // Fetch data with restored filters - use the values directly from filters object
      fetchSchedules({
        fseId: filters.filterFseId || null,
        clientId: filters.filterClientId || null,
        householdId: filters.filterHouseholdId || null,
        quoteStatus: filters.filterQuoteStatus || null // Use null instead of empty string for consistency
      }, 1, false);
      
      // Clear navigation state to prevent re-applying on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      // No filters, fetch all data
      fetchSchedules({}, 1, false);
    }
  }, [location.state, navigate, fetchSchedules]);

  // --- End persist filters ---

  // --- Search Hooks for Filters ---
  const franchiseeSearch = useSearch(searchFranchisees);
  const supplierSearch = useSearch(searchSuppliers);
  const appClientSearch = useSearch(searchAppClients);
  // --- End Search Hooks ---

  const { loading: configLoading, error: configError, getStatuses } = useAppConfig();
  const jobStatuses = useMemo(() => getStatuses() || [], [getStatuses]);

  useEffect(() => {
    // Clear success message
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        navigate(location.pathname, { replace: true, state: {} });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, navigate, location.pathname]);

  // Ref to track if initial mount has completed
  const isInitialMount = useRef(true);

  // Trigger data fetch when sorting changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    console.log('[JobSchedulerPage] Sorting changed:', { sortBy, sortDirection });
    // Fetch data with current filters and new sorting
    fetchSchedules({
      fseId: filterFseId,
      clientId: filterClientId,
      householdId: filterHouseholdId,
      quoteStatus: filterQuoteStatus || null
    }, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDirection]);

  // --- Filter Handlers ---
  const handleApplyFilters = () => {
    setNoMoreResults(false);
    // Fetch data with the current filter state
    fetchSchedules({ 
      fseId: filterFseId, 
      clientId: filterClientId, 
      householdId: filterHouseholdId,
      quoteStatus: filterQuoteStatus || null // Convert empty string to null
    }, 1, false);
  };

  const handleClearFilters = () => {
    setFilterFseId(null);
    setFilterClientId(null);
    setFilterHouseholdId(null);
    setFilterQuoteStatus('');
    setFseSearchTerm('');
    setClientSearchTerm('');
    setHouseholdSearchTerm('');
    // Fetch all data without filters
    setNoMoreResults(false);
    fetchSchedules({}, 1, false);
  };

  // --- Sorting Handler ---
  const handleSortChange = (field) => {
    console.log('[JobSchedulerPage] handleSortChange called with field:', field);
    let newDirection;
    if (sortBy === field) {
      // Toggle direction if clicking same field
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      console.log('[JobSchedulerPage] Toggling direction:', sortDirection, '->', newDirection);
      setSortDirection(newDirection);
    } else {
      // Set new field with default direction
      setSortBy(field);
      // Default directions based on field
      if (field === 'createdAt') {
        newDirection = 'desc'; // Newest first for date created
      } else {
        newDirection = 'asc'; // A-Z or oldest first for others
      }
      console.log('[JobSchedulerPage] Setting new field:', field, 'with direction:', newDirection);
      setSortDirection(newDirection);
    }
    // Reset pagination when sorting changes
    setCurrentPage(1);
    setPageStartAfterIds({ 1: null });
  };


  const handleEdit = (id) => {
    // Pass current filters and sorting in location.state
    const filters = {
      filterFseId,
      filterClientId,
      filterHouseholdId,
      filterQuoteStatus,
      fseSearchTerm,
      clientSearchTerm,
      householdSearchTerm,
      sortBy,
      sortDirection,
    };
    navigate(`/job-scheduler/edit/${id}`, { state: { filters } });
  };

  // Generic search handler creation (copied from JobsListPage)
  const createFilterSearchHandlers = (searchState, setFilterId, setSearchTermDisplay) => ({
      onChange: (e) => {
          const value = e.target.value;
          searchState.setSearchTerm(value);
          setSearchTermDisplay(value);
          setFilterId(null); // Clear the ID when search term changes
          if (value.length < 3) searchState.setShowSuggestions(false);
      },
      onSelect: (item) => {
          setFilterId(item.id);
          let displayName;
          if (searchState === franchiseeSearch) {
              const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
              displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
          } else if (searchState === appClientSearch) {
              displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'N/A';
          } else { // supplierSearch
              displayName = item.clientName || '';
          }
          setSearchTermDisplay(displayName);
          searchState.setSuggestions([]);
          searchState.setShowSuggestions(false);
      },
      renderSuggestions: (item) => {
          if (searchState === franchiseeSearch) {
              const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
              return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
          } else if (searchState === appClientSearch) {
              return `${item.firstName || ''} ${item.lastName || ''}`.trim() + (item.addressLine1 ? ` (${item.addressLine1})` : '');
          } else { // supplierSearch
              return item.clientName || '';
          }
      }
  });

  const fseFilterHandlers = createFilterSearchHandlers(franchiseeSearch, setFilterFseId, setFseSearchTerm);
  const supplierFilterHandlers = createFilterSearchHandlers(supplierSearch, setFilterClientId, setClientSearchTerm);
  const appClientFilterHandlers = createFilterSearchHandlers(appClientSearch, setFilterHouseholdId, setHouseholdSearchTerm);

  // Close suggestions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Close any potential dropdowns here if needed in the future
      }
      // Close search suggestions if click is outside relevant areas
      if (!event.target.closest('.filter-section')) {
         franchiseeSearch.setShowSuggestions(false);
         supplierSearch.setShowSuggestions(false);
         appClientSearch.setShowSuggestions(false);
      }
    };
    // Add listener if any suggestions are shown
    if (franchiseeSearch.showSuggestions || supplierSearch.showSuggestions || appClientSearch.showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [franchiseeSearch.showSuggestions, supplierSearch.showSuggestions, appClientSearch.showSuggestions]);
  // --- End Filter Handlers ---


  const handleAddSchedule = () => {
    // Pass current filters and sorting when navigating to add page
    const filters = {
      filterFseId,
      filterClientId,
      filterHouseholdId,
      filterQuoteStatus,
      fseSearchTerm,
      clientSearchTerm,
      householdSearchTerm,
      sortBy,
      sortDirection,
    };
    navigate('/job-scheduler/add', { state: { filters } });
  };

  const handleDelete = async (schedule) => {
    console.log("Delete schedule initiated:", schedule.id);
    
    // Set loading state for this specific schedule
    setFetchingJobs(true);
    setDeleteError(null);
    
    try {
      // Fetch jobs for this schedule
      console.log("Fetching jobs for schedule:", schedule.id);
      const jobs = await getJobsByScheduleId(schedule.id);
      console.log(`Found ${jobs.length} jobs for schedule ${schedule.id}:`, jobs);
      
      // Check if any jobs are completed or invoiced
      const completedJobs = jobs.filter(
        job => job.status === 'completed' || job.status === 'invoiced'
      );
      console.log(`Completed jobs: ${completedJobs.length}`, completedJobs);
      
      if (completedJobs.length > 0) {
        // Show error modal - cannot delete schedule with completed jobs
        setErrorModalMessage(
          `Cannot delete this schedule: it has ${completedJobs.length} completed job(s). ` +
          `Schedules with completed jobs cannot be deleted for record-keeping purposes.`
        );
        setErrorModalOpen(true);
        setFetchingJobs(false);
        return;
      }
      
      // No completed jobs - proceed to show confirmation modal
      setScheduleToDelete(schedule);
      setJobsToDelete(jobs);
      setDeleteModalOpen(true);
      
    } catch (err) {
      console.error("Error fetching jobs for delete:", err);
      setErrorModalMessage(`Failed to load jobs: ${err.message}`);
      setErrorModalOpen(true);
    } finally {
      setFetchingJobs(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!scheduleToDelete) return;
    
    setDeleteLoading(true);
    setDeleteError(null);
    
    try {
      const result = await deleteSchedule(scheduleToDelete.id);
      
      // Success - close modal and refresh list
      setDeleteModalOpen(false);
      setScheduleToDelete(null);
      setJobsToDelete([]);
      
      // Show success message
      const deletedCount = result.data?.deletedJobsCount || 0;
      setSuccessMessage(
        `Schedule deleted successfully! ${deletedCount} job(s) were also deleted.`
      );
      
      // Refresh the schedules list
      fetchSchedules({
        fseId: filterFseId,
        clientId: filterClientId,
        householdId: filterHouseholdId,
        quoteStatus: filterQuoteStatus || null
      }, 1, false);
      
      // Auto-clear success message
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (err) {
      console.error("Error deleting schedule:", err);
      setDeleteError(err.message || 'Failed to delete schedule');
      // Keep modal open to show error
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setScheduleToDelete(null);
    setJobsToDelete([]);
    setDeleteError(null);
  };

  const renderScheduleDetails = (schedule) => {
    let details = [];
    if (schedule.scheduleType === 'one-off') {
        details.push(`One-off: ${formatTimestamp(schedule.jobDate, false)}`);
    } else if (schedule.scheduleType === 'recurring') {
        // Use updated formatFrequency which now handles lowercase and null type
        details.push(`Recurring: ${formatters.formatFrequency(schedule.frequencyType, schedule.frequencyValue)}`);
        details.push(`Starts: ${formatTimestamp(schedule.startDate)}`);
        if (schedule.endDate) {
            details.push(`Ends: ${formatTimestamp(schedule.endDate)}`);
        }
        // Add occurrences if present
        if (schedule.occurrences != null) {
             details.push(`Occurrences: ${schedule.occurrences}`);
        }
    } else {
        details.push(`Type: ${schedule.scheduleType || 'N/A'}`);
    }
    return details.join(' | ');
  };

  // Remove the old useEffect for restoreFilters since we now handle it in the main initialization

  // Add pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) {
      setLoadingDirection('prev');
      setNoMoreResults(false);
      fetchSchedules({ 
        fseId: filterFseId, 
        clientId: filterClientId, 
        householdId: filterHouseholdId,
        quoteStatus: filterQuoteStatus || null // Convert empty string to null
      }, currentPage - 1, true).finally(() => {
        setLoadingDirection(null);
      });
    }
  };

  const handleNextPage = () => {
    if (hasNextPage && !loading) {
      setLoadingDirection('next');
      setNoMoreResults(false);
      fetchSchedules({
        fseId: filterFseId,
        clientId: filterClientId,
        householdId: filterHouseholdId,
        quoteStatus: filterQuoteStatus || null // Convert empty string to null
      }, currentPage + 1, true).finally(() => {
        setLoadingDirection(null);
      });
    }
  };

  // --- Sorting Helpers ---
    const getSortIcon = (field) => {
      if (sortBy !== field) return <FaSort className="inline ml-1 text-gray-400" />;
      return sortDirection === 'asc'
        ? <FaSortUp className="inline ml-1 text-white" />
        : <FaSortDown className="inline ml-1 text-white" />;
    };
  
    const activeSortOption = sortFieldOptions.find(option => option.id === sortBy);
    const activeSortDescriptor = activeSortOption
      ? `${directionLabels[sortDirection]} (${getDirectionDescriptor(activeSortOption.valueType, sortDirection)})`
      : null;
    // --- End Sorting Helpers ---
  

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Job Scheduler</h1>
        <div className="flex items-center space-x-4">
          <Button onClick={handleAddSchedule} variant="primary">
            <FaPlus className="mr-2" />
            Schedule Job(s)
          </Button>
          <Link to="/" className="text-gray-900 hover:text-green-600 underline">
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-3xl shadow-sm ring-1 ring-gray-900/5 mb-4 filter-section transition-all duration-300">
        <div className="flex justify-between items-center cursor-pointer pb-2" onClick={() => setIsFilterExpanded(!isFilterExpanded)}>
           <h2 className="text-lg font-semibold text-gray-700 flex items-center">
              <FaFilter className="mr-2 text-green-600"/>Sort & Filter
           </h2>
           <button type="button" className="text-gray-500 hover:text-gray-700 focus:outline-none">
             {isFilterExpanded ? <FaChevronUp /> : <FaChevronDown />}
           </button>
        </div>
        
        {isFilterExpanded && (
          <div className="mt-2 border-t border-gray-100 pt-4">
            {/* --- Sorting Section (at top of filter card) --- */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-600 mb-3 flex items-center">
                <FaSort className="mr-2 text-gray-400"/>Sort By
              </h2>
              <div className="flex flex-wrap gap-2">
            <button
              type='button'
              onClick={() => handleSortChange('franchiseeInfo.franchiseName')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition duration-200 ${
                sortBy === 'franchiseeInfo.franchiseName'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Franchisee {getSortIcon('franchiseeInfo.franchiseName')}
            </button>
            <button
              type='button'
              onClick={() => handleSortChange('clientInfo.clientName')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition duration-200 ${
                sortBy === 'clientInfo.clientName'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {terminology.client.singular} {getSortIcon('clientInfo.clientName')}
            </button>
            <button
              type='button'
              onClick={() => handleSortChange('householdInfo.firstName')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition duration-200 ${
                sortBy === 'householdInfo.firstName'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {terminology.household.singular} {getSortIcon('householdInfo.firstName')}
            </button>
            <button
              onClick={() => handleSortChange('createdAt')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition duration-200 ${
                sortBy === 'createdAt'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Date Created {getSortIcon('createdAt')}
            </button>
          </div>
           {activeSortOption && activeSortDescriptor && (
            <p className="mt-2 text-xs text-gray-600" role="status">
              Sorted by {activeSortOption.label} · {activeSortDescriptor}
            </p>
          )}
        </div>
        {/* --- End Sorting Section --- */}

        <h2 className="text-sm font-semibold text-gray-600 mb-2 flex items-center">
          <FaSearch className="mr-2 text-gray-400"/>Filters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3"> {/* Changed to md:grid-cols-4 */}
          {/* Franchisee Filter */}
          <div className="relative">
            <label htmlFor="fseFilter" className="block text-xs font-medium text-gray-600">Franchisee</label>
            <input
              id="fseFilter"
              type="text"
              autoComplete="off"
              value={fseSearchTerm}
              onChange={fseFilterHandlers.onChange}
              onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
              onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
            {franchiseeSearch.showSuggestions && (
              <ul className="suggestions-list filter-suggestions">
                {franchiseeSearch.isSearching ? (
                  <li className="searching">Searching...</li>
                ) : franchiseeSearch.suggestions.length > 0 ? (
                  franchiseeSearch.suggestions.map(item => (
                    <li 
                      key={item.id} 
                      className="suggestion-item" 
                      onMouseDown={() => fseFilterHandlers.onSelect(item)}
                    >
                      {fseFilterHandlers.renderSuggestions(item)}
                    </li>
                  ))
                ) : franchiseeSearch.searchTerm.length >= 3 && (
                  <li className="no-results">No franchisees found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Supplier (Client) Filter */}
          <div className="relative">
            <label htmlFor="clientFilter" className="block text-xs font-medium text-gray-600">{terminology.client.singular}</label>
            <input
              id="clientFilter"
              type="text"
              autoComplete="off"
              value={clientSearchTerm}
              onChange={supplierFilterHandlers.onChange}
              onBlur={() => setTimeout(() => supplierSearch.setShowSuggestions(false), 150)}
              onFocus={() => supplierSearch.suggestions.length > 0 && supplierSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
            {supplierSearch.showSuggestions && (
              <ul className="suggestions-list filter-suggestions">
                {supplierSearch.isSearching ? (
                  <li className="searching">Searching...</li>
                ) : supplierSearch.suggestions.length > 0 ? (
                  supplierSearch.suggestions.map(item => (
                    <li 
                      key={item.id} 
                      className="suggestion-item" 
                      onMouseDown={() => supplierFilterHandlers.onSelect(item)}
                    >
                      {supplierFilterHandlers.renderSuggestions(item)}
                    </li>
                  ))
                ) : supplierSearch.searchTerm.length >= 3 && (
                  <li className="no-results">No {terminology.client.plural.toLowerCase()} found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Client (Household) Filter */}
          <div className="relative">
            <label htmlFor="householdFilter" className="block text-xs font-medium text-gray-600">
              {terminology.household.singular}
            </label>
            <input
              id="householdFilter"
              type="text"
              autoComplete="off"
              value={householdSearchTerm}
              onChange={appClientFilterHandlers.onChange}
              onBlur={() => setTimeout(() => appClientSearch.setShowSuggestions(false), 150)}
              onFocus={() => appClientSearch.suggestions.length > 0 && appClientSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
            {appClientSearch.showSuggestions && (
              <ul className="suggestions-list filter-suggestions">
                {appClientSearch.isSearching ? (
                  <li className="searching">Searching...</li>
                ) : appClientSearch.suggestions.length > 0 ? (
                  appClientSearch.suggestions.map(item => (
                    <li 
                      key={item.id} 
                      className="suggestion-item" 
                      onMouseDown={() => appClientFilterHandlers.onSelect(item)}
                    >
                      {appClientFilterHandlers.renderSuggestions(item)}
                    </li>
                  ))
                ) : appClientSearch.searchTerm.length >= 3 && (
                  <li className="no-results">
                    No {terminology.household.plural.toLowerCase()} found.
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Quote Status Filter */}
          <div className="relative">
            <label htmlFor="quoteStatusFilter" className="block text-xs font-medium text-gray-600">Quote Status</label>
            <select
              id="quoteStatusFilter"
              value={filterQuoteStatus}
              onChange={(e) => setFilterQuoteStatus(e.target.value)}
              className="block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
            >
              <option value="">All Statuses</option>
              <option value="pending-quote">Pending Quote</option>
              <option value="quote-received">Quote Received</option>
              <option value="pending-approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="not-approved">Not Approved</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            onClick={handleClearFilters}
            variant="secondary"
            disabled={loading}
          >
            <FaTimes className="mr-1" /> Clear
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="primary"
            disabled={loading || franchiseeSearch.isSearching || supplierSearch.isSearching || appClientSearch.isSearching}
          >
            <FaFilter className="mr-1" /> Apply
          </Button>
        </div>
        </div>
        )}
      </div>
      {/* Show loading indicator above the list instead of replacing it */}
      {loading && <p className="text-center text-gray-500 py-2">Loading...</p>}
      {error && <p className="text-center text-red-500 bg-red-100 p-3 rounded-xl">Error: {error}</p>}

      {!error && (
        <>
          <div className="space-y-4">
            {schedules.length === 0 && !noMoreResults ? (
              <p className="text-center text-gray-500 p-6 bg-white rounded-xl shadow">
                No job schedules found{filterFseId || filterClientId || filterHouseholdId || filterQuoteStatus ? ' matching your filters' : ''}.
              </p>
            ) : (
              schedules.map((schedule) => {
                // Log allocation data for days/weeks to trace the issue
                if (schedule.allocationType === 'days' || schedule.allocationType === 'weeks') {
                  console.log(`[JobSchedulerPage] Schedule ${schedule.id}: allocationType="${schedule.allocationType}", allocationValue=${schedule.allocationValue}`);
                }
                
                return (
                <div key={schedule.id} className="shadow-sm ring-1 ring-gray-900/5 rounded-3xl p-4 bg-white">
                  {/* Row 1: Entity Info (Remains at the top) */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3 pb-3">
                      <div>
                          <p className="font-semibold mb-1">
                              <FaUserTie className="inline mr-1 text-gray-500"/>
                              Franchisee {schedule.franchiseeInfo?.fseCode ? `(${schedule.franchiseeInfo.fseCode})` : ''}
                          </p>
                          <p>{(schedule.franchiseeInfo?.franchiseName || 'N/A')}</p>
                          <p className="text-xs text-gray-500">
                              {`${schedule.franchiseeInfo?.firstName || ''} ${schedule.franchiseeInfo?.lastName || ''}`.trim()}
                          </p>
                      </div>
                      <div>
                          <p className="font-semibold mb-1"><FaUser className="inline mr-1 text-gray-500"/>{terminology.client.singular}</p>
                          <p>{(schedule.clientInfo?.clientName || 'N/A')}</p>
                      </div>
                      <div>
                          <p className="font-semibold mb-1"><FaHome className="inline mr-1 text-gray-500"/>{terminology.household.singular}</p>
                          <p>{(`${schedule.householdInfo?.firstName || ''} ${schedule.householdInfo?.lastName || ''}`.trim() || 'N/A')}</p>
                          <p className="text-xs text-gray-500">
                              <FaMapMarkerAlt className="inline mr-1"/>
                              {(() => { const raw = `${schedule.householdInfo?.addressLine1 || ''}${schedule.householdInfo?.addressLine2 ? ', ' + schedule.householdInfo.addressLine2 : ''}, ${schedule.householdInfo?.state || ''} ${schedule.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address'; return raw; })()}
                          </p>
                      </div>
                  </div>

                  {/* Site Contact Info Row (if present) */}
                  {(schedule.householdInfo?.siteContact?.name || schedule.householdInfo?.siteContact?.email || schedule.householdInfo?.siteContact?.phone) && (
                    <div className="bg-gray-100 rounded-2xl px-4 py-3 mb-2 flex flex-wrap gap-6 items-center">
                      {schedule.householdInfo?.siteContact?.name && (
                        <div className="min-w-[160px] flex-1">
                          <span className="block text-xs font-semibold text-gray-600">Case Manager</span>
                          <span className="block text-sm text-gray-800">{schedule.householdInfo.siteContact.name}</span>
                        </div>
                      )}
                      {schedule.householdInfo?.siteContact?.email && (
                        <div className="min-w-[160px] flex-1">
                          <span className="block text-xs font-semibold text-gray-600">Email</span>
                          <span className="block text-sm text-gray-800">{schedule.householdInfo.siteContact.email}</span>
                        </div>
                      )}
                      {schedule.householdInfo?.siteContact?.phone && (
                        <div className="min-w-[160px] flex-1">
                          <span className="block text-xs font-semibold text-gray-600">Phone</span>
                          <span className="block text-sm text-gray-800">{schedule.householdInfo.siteContact.phone}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status Row (Moved below entity info) */}
                  <div className="border-t border-gray-200 pt-3 mb-3">
                      <div className="flex justify-between items-start">
                          <div className="text-sm text-gray-700 space-y-1">
                              {/* Status and Quote Status Pills */}
                              <div className="flex flex-wrap gap-2">
                                  {/* Status Pill */}
                                  {schedule.status && (
                                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusPillClasses(getStatusDetails(schedule.status, jobStatuses).color)}`}>
                                          {getStatusDetails(schedule.status, jobStatuses).display}
                                      </span>
                                  )}
                                  {/* Quote Status Pill */}
                                  {schedule.quoteStatus && (
                                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusPillClasses(getQuoteStatusDetails(schedule.quoteStatus, jobStatuses).color)}`}>
                                          {getQuoteStatusDetails(schedule.quoteStatus, jobStatuses).display}
                                      </span>
                                  )}
                              </div>
                          </div>
                          {/* Edit/Delete Buttons */}
                          <div className="flex space-x-2">
                              <button onClick={() => handleEdit(schedule.id)} className="text-green-600 hover:text-green-700 transition duration-300 p-1">
                                  <FaEdit size="1.1em"/>
                              </button>
                              <button 
                                onClick={() => handleDelete(schedule)} 
                                disabled={fetchingJobs}
                                className="text-red-500 hover:text-red-700 transition duration-300 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="Delete schedule"
                              >
                                {fetchingJobs ? (
                                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <FaTrashAlt size="1.1em"/>
                                )}
                              </button>
                          </div>
                      </div>
                  </div>                  {/* Row 2: Schedule Details and Time */}
                  <div className="text-sm text-gray-700 space-y-1 mb-3">
                      <p className="font-semibold capitalize"> {/* Ensure capitalize is applied */}
                          <FaCalendarAlt className="inline mr-2 text-green-600" />
                          {renderScheduleDetails(schedule)} {/* Uses updated render logic */}
                          {schedule.ndisJob && ( // NDIS Job indicator
                              <span className="ml-3 inline-flex items-center"> 
                                  <FaMedkit className="inline mr-1 text-green-600" />
                                  <span className="text-green-600 font-medium">NDIS</span>
                              </span>
                          )}
                          {schedule.jobTime && ( // Only show if jobTime exists
                              <span className="ml-3"> {/* Added margin-left */}
                                  <FaClock className="inline mr-1 text-green-600" />
                                  Preferred Time: {schedule.jobTime}
                              </span>
                          )}
                      </p>
                  </div>

                  {/* Row 3 (WAS Row 4): Allocation, Hours, Rates */}
                  <div className="text-sm text-gray-700 space-y-1 mb-3">
                      <p>
                          {schedule.allocationType && (
                              <span className="mr-3 inline-flex items-center">
                                  <FaLayerGroup className="mr-1 text-green-600" />
                                  Allocation: {formatters.formatAllocationType(schedule.allocationType, schedule.allocationValue)}
                              </span>
                          )}
                          {/* Display Hours (Adjust label for fixed-fee) */}
                          {schedule.allotedHours != null && (
                              <span className="mr-3 inline-flex items-center">
                                  <FaHourglassHalf className="mr-1 text-green-600" />
                                  {schedule.allocationType === 'monthly' ? 'Allocation/month' :
                                   schedule.allocationType === 'per-visit' ? 'Allocation/visit' :
                                   schedule.allocationType === 'fixed-fee' ? 'Fixed allocated hours' : // Adjusted label
                                   'Allotted'}: {schedule.allotedHours} hr(s)
                              </span>
                          )}
                          {/* Display FSE Rate (Price/Hour) */}
                           {schedule.pricePerHour != null && (
                              <span className="mr-3 inline-flex items-center">
                                  <FaDollarSign className="mr-1 text-green-600" />
                                  FSE Rate: ${schedule.pricePerHour.toFixed(2)} {schedule.allocationType !== 'fixed-fee' && '/ hr'}
                              </span>
                          )}
                          {/* Display Invoice Price */}
                           {schedule.additionalPrice != null && schedule.additionalPrice > 0 && (
                              <span className="mr-3 inline-flex items-center">
                                  <FaFileInvoiceDollar className="mr-1 text-green-600" />
                                  Invoice (ex. GST): ${schedule.additionalPrice.toFixed(2)} {schedule.allocationType !== 'fixed-fee' && '/ hr'}
                              </span>
                          )}
                          {/* Display Invoice Rate (Invoice Price/Hour) */}
                           {schedule.invoicePricePerHour != null && (
                              <span className="mr-3 inline-flex items-center">
                                  <FaFileInvoiceDollar className="mr-1 text-green-600" />
                                  Invoice Rate: ${schedule.invoicePricePerHour.toFixed(2)} {schedule.allocationType !== 'fixed-fee' && '/ hr'}
                              </span>
                          )}
                      </p>
                  </div>

                  {/* Row 4 (WAS Row 5): Services */}
                  {schedule.services && schedule.services.length > 0 && (
                      <div className="text-sm text-gray-700 space-y-1 mb-3"> {/* Changed from text-xs to text-sm to match allocation values */}
                          <p>
                              <FaTasks className="inline mr-2 text-green-600" />
                              Services: {
                                  // Check if services is an array of objects with 'name' property
                                  Array.isArray(schedule.services) && schedule.services.every(s => typeof s === 'object' && s !== null && 'name' in s)
                                    ? schedule.services.map(s => s.name).join(', ')
                                    : Array.isArray(schedule.services) // Fallback for array of strings (old data)
                                    ? schedule.services.join(', ')
                                    : 'N/A' // Handle unexpected format
                            }
                          </p>
                      </div>
                  )}

                  {/* Noted From Franchisee (below services) */}
                  {schedule.notesFromFranchisee && (
                      <div className="text-xs text-gray-700 space-y-1 mb-3">
                          <p>
                              <FaStickyNote className="inline mr-2 text-green-600" />
                              Noted From Franchisee: <span className="text-gray-600 italic">{schedule.notesFromFranchisee}</span>
                          </p>
                      </div>
                  )}

                  {/* Row 5 (WAS Row 6): Description (Invoice Notes) */}
                  {schedule.description && (
                      <div className="text-sm text-gray-700 space-y-1 mb-3">
                          <p>
                              <FaStickyNote className="inline mr-2 text-green-600" />
                              Description (Invoice): <span className="text-gray-600 italic">{schedule.description}</span>
                          </p>
                      </div>
                  )}

                  {/* Row 6: Admin Notes */}
                  {schedule.adminNotes && (
                      <div className="text-sm text-gray-700 space-y-1 mb-3">
                          <p>
                              <FaStickyNote className="inline mr-2 text-orange-500" />
                              Admin Notes: <span className="text-gray-600 italic">{schedule.adminNotes}</span>
                          </p>
                      </div>
                  )}

                  {/* Row 7 (WAS Row 7): Notes for Franchisee */}
                  {schedule.notesForFranchisee && (
                      <div className="text-sm text-gray-700 space-y-1 mb-3">
                          <p>
                              <FaStickyNote className="inline mr-2 text-green-600" />
                              Notes for FSE: <span className="text-gray-600 italic">{schedule.notesForFranchisee}</span>
                          </p>
                      </div>
                  )}

                  {/* Row 8: Created/Updated Dates */}
                  <div className="text-xs text-gray-500 flex flex-wrap gap-4 border-t pt-2 mt-2">
                    {schedule.createdAt && (
                      <span>
                        Created: {formatTimestamp(schedule.createdAt, true)}
                      </span>
                    )}
                    {schedule.updatedAt && (
                      <span>
                        Updated: {formatTimestamp(schedule.updatedAt, true)}
                      </span>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>

          {/* Show pagination controls only if we have schedules or we're not on page 1 */}
          {(schedules.length > 0 || currentPage > 1) && (
            <div className="mt-6 flex justify-center items-center space-x-4">
              <Button
                onClick={handlePrevPage}
                disabled={currentPage <= 1 || loading}
                variant="secondary"
              >
                {loadingDirection === 'prev' ? 'Loading...' : <><FaArrowLeft className="mr-2" /> Previous</>}
              </Button>
              <span className="text-gray-700">Page {currentPage}</span>
              <Button
                onClick={handleNextPage}
                disabled={!hasNextPage || loading || noMoreResults}
                variant="secondary"
              >
                {loadingDirection === 'next' ? 'Loading...' : <>Next <FaArrowRight className="ml-2" /></>}
              </Button>
            </div>
          )}
        </>
      )}
      
      {/* Add Styles */}
      <style jsx>{`
            .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 {
                padding: 0.5rem 0.75rem;
                border-width: 1px;
                border-radius: 1rem;
                box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                outline: none;
                transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
            }
            .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6:focus {
                border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.3);
            }
            .suggestions-list {
                position: absolute;
                z-index: 10;
                width: 100%;
                background-color: white;
                border: 1px solid #D1D5DB; border-radius: 1rem;
                margin-top: 0.25rem;
                max-height: 15rem;
                overflow-y: auto;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }
            .suggestion-item {
                padding: 0.5rem 0.75rem;
                cursor: pointer;
            }
            .suggestion-item:hover {
                background-color: #67E8F9;
            }
            .searching, .no-results {
                 padding: 0.5rem 0.75rem;
                 color: #6B7280;
            }
            .btn {
                padding: 0.5rem 1rem;
                font-weight: 700;
                border-radius: 0.375rem;
                transition: background-color 0.3s ease, opacity 0.3s ease;
                border: 1px solid transparent;
                cursor: pointer;
            }
            .btn:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
            .btn-primary {
                background-color: #06B6D4;
                color: white;
            }
            .btn-primary:hover:not(:disabled) {
                background-color: #0E7490;
            }
            .btn-secondary {
                background-color: #E5E7EB;
                color: #1F2937;
            }
            .btn-secondary:hover:not(:disabled) {
                 background-color: #D1D5DB;
            }

            /* Styles for Filter Section */
            .filter-section .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6.filter-input,
            .filter-section .filter-btn {
                padding: 0.35rem 0.6rem; /* Reduced padding */
                font-size: 0.875rem; /* text-sm */
            }
            .filter-section .filter-btn {
                 font-weight: 600; /* Slightly less bold */
            }

            .filter-section .suggestions-list.filter-suggestions {
                margin-top: 0.1rem; /* Tighter spacing */
                max-height: 10rem; /* Smaller max height */
            }
             .filter-section .suggestions-list.filter-suggestions .suggestion-item,
             .filter-section .suggestions-list.filter-suggestions .searching,
             .filter-section .suggestions-list.filter-suggestions .no-results {
                 padding: 0.35rem 0.6rem; /* Reduced padding */
                 font-size: 0.875rem; /* text-sm */
             }
        `}</style>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModalOpen}
        title="Cannot Delete Schedule"
        message={errorModalMessage}
        onClose={() => setErrorModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      <DeleteScheduleModal
        isOpen={deleteModalOpen}
        schedule={scheduleToDelete}
        jobs={jobsToDelete}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deleteLoading}
        error={deleteError}
      />
    </div>
  );
};

export default JobSchedulerPage;
